import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, NavLink } from "react-router-dom";
import examples from 'libphonenumber-js/mobile/examples';
import { AsYouType, getCountryCallingCode, parsePhoneNumber, getExampleNumber } from 'libphonenumber-js';
import { put } from '../../utils/api';
import { Form, FormLine, FormItem, FormInput } from '../common/Form';
import { getCountriesAlpha3Codes, getCountryName, convertAlpha3ToAlpha2 } from '../../utils/countries';

const ExhibitorEditContacts = () => {
	const { t, i18n } = useTranslation();
    const lang = i18n.language;
	const navigate = useNavigate();
    const exhibitor = useSelector(state => state.exhibitor);
    const [ cntrs, setCntrs ] = useState(null);
	const [ country, setCountry ] = useState(exhibitor.contacts ? exhibitor.contacts.country : '');
	const [ city_ru, setCityRu ] = useState(exhibitor.contacts ? exhibitor.contacts.city.ru : '');
	const [ city_en, setCityEn ] = useState(exhibitor.contacts ? exhibitor.contacts.city.en : '');
    const [ phone, setPhone ] = useState(exhibitor.contacts ? exhibitor.contacts.phone : '');
    const [ phoneExample, setPhoneExample ] = useState('+7 912 123 45 67');
    const [ email, setEmail ] = useState(exhibitor.contacts ? exhibitor.contacts.email : '');
    const [ www, setWww] = useState(exhibitor.contacts ? exhibitor.contacts.www : '');
    const [ success, setSuccess ] = useState('');
    const [ isSaving, setIsSaving ] = useState(false);
    const [ saveError, setSaveError ] = useState('');

    // Сортируем массив для выбора страны
    // Россия всегда на первом месте
    useEffect(() => {
        const codes = getCountriesAlpha3Codes();
        const cntrs = [];
        Object.keys(codes).forEach(code => {
            cntrs.push({ value: code, text: getCountryName(code, lang) });
        });
        cntrs.sort((a, b) => {
            if(a.value === 'RUS') return -1;
            if(b.value === 'RUS') return  1;
            return (a.text).localeCompare(b.text);
        });
        setCntrs(cntrs);
    }, [lang]);

    // При смене страны сбрасываем телефон и печаем код страны
    const checkCountry = (country) => {
        // Мы храним в базе трехзначный код страны, но валидатор
        // номера телефона работает только с двузначными кодами.
        const cc = convertAlpha3ToAlpha2(country);
        const code = getCountryCallingCode(cc);
        setPhone('+' + code);
        // Добавим в плейсхолдер пример номера в формате страны
        const exampleNumber = getExampleNumber(cc, examples);
        setPhoneExample(exampleNumber.formatInternational());
        // Запишем смену страны
        setCountry(country);
    };

    // Проверка номера телефона при наборе
    const checkPhone = (phone) => {        
        phone = phone.trim();
        if(!phone) {
            setPhone(phone);
            return;
        }
        // Оставим только цифры и добавим + в начало.
        // Без плюса библиотека будет воспринимать номер как локальный
        // с учетом установленной по умолчанию страны. А она не факт что
        // будет совпадать с вводимым номером.
        phone = phone.replace(/[^\d]/g, '');
        phone = phone.substring(0, 18); // максимальное число цифр в номере
        phone = '+' + phone;
        // Отпарсим введенный номер
        try {
            const phoneNumber = parsePhoneNumber(phone);
            if(phoneNumber) {
                // Определим код страны и пример номера
                const exampleNumber = getExampleNumber(phoneNumber.country, examples);
                const example = exampleNumber.formatInternational();
                setPhoneExample(example);
                // Узнаем длину номера для данного кода страны
                const length = example.replace(/\s+/g, '').length;
                // Обрежем введенный номер с учетом максимальной длины
                phone = phone.substring(0, length);
            }
        } catch(error) {
            // Пока не удалось отпарсить, т.е. ввели слишком мало цифр.
            // Выводим то, что есть и ждем дальнейшего ввода.
        }
        // Выводим что получилось
        setPhone(new AsYouType().input(phone));
    };

    // Отправка данных
	const onSubmit = async () => {
        setIsSaving(true);
        setSaveError('');
        setSuccess('');

        let web = www.trim();
        if(web && !web.match(/^https?:\/\//i)) {
            web = 'https://' + web;
        }

        const contacts = {
            country,
            city_ru,
            city_en,
            phone,
            email: email + ' ',
            www: web + ' ',
        };

        const result = await put('/exhibitors/' + exhibitor._id, { contacts });

        if(result.error) {
            setSaveError(result.error);
        } else {
            setSuccess('exhibitoredit.success');
			navigate('/cabinet/exhibitorview/' + exhibitor._id + '/action/updated');
        }
        
        setIsSaving(false);
	};

    return (
		<div>
	    	<h1>{t('exhibitoredit.title_contacts')}</h1>
			{isSaving ? <span>{t('common.loading')}</span> :
			<Form
                success={success}
                error={saveError}
                info='exhibitoredit.subtitle'
                wide={true}
                submit={t('common.save_changes')}
				onSubmit={onSubmit}
            >
				<FormInput
					id='country'
                    type='select'
                    list={cntrs}
					value={country}
					onChange={checkCountry}
                    label={t('exhibitoredit.country')}
                    hint={t('exhibitoredit.country_hint')}
                    required={true}
                />

                <FormLine>
                    <FormItem>
                        <FormInput
                            id='city_ru'
                            type='text'
                            value={city_ru}
                            onChange={setCityRu}
                            placeholder={t('exhibitoredit.city_ru')}
                            className='lang-ru'
                            label={t('exhibitoredit.city_ru')}
                            required={true}
                        />
                    </FormItem>
                    <FormItem>
                        <FormInput
                            id='city_en'
                            type='text'
                            value={city_en}
                            onChange={setCityEn}
                            placeholder={t('exhibitoredit.city_en')}
                            className='lang-en'
                            label={t('exhibitoredit.city_en')}
                            required={true}
                        />
                    </FormItem>
                </FormLine>

                <FormLine>
                    <FormItem>
                        <FormInput
                            id='phone'
                            type='text'
                            value={phone}
                            onChange={checkPhone}
                            placeholder={phoneExample}
                            label={t('exhibitoredit.phone')}
                            required={true}
                        />
                    </FormItem>
                    <FormItem>
                        <FormInput
                            id='email'
                            type='text'
                            value={email}
                            onChange={setEmail}
                            placeholder='yourcompany@example.ru'
                            label={t('exhibitoredit.email')}
                        />
                    </FormItem>
                </FormLine>

				<FormInput
					id='www'
                    type='text'
					value={www}
					onChange={setWww}
                    placeholder='www.example.ru'
                    label={t('exhibitoredit.www')}
				/>
			</Form>}

            <NavLink to={'/cabinet/exhibitorview/' + exhibitor._id}>
                {t('exhibitoredit.back')}
            </NavLink>
		</div>
	);
};

export default ExhibitorEditContacts;
